// src/components/MealCard.js

import React from 'react';
import { Card, Button, Tooltip } from '../../../shared/components/ui';
import { useTranslation } from 'react-i18next';

const MealCard = ({
    meal,
    mealKey,
    canGiveFeedback = false,
    canReorder = false,
    evaluatedMeals = {},
    repeatableStates = {},
    onFeedback,
    onToggleRepeatable,
    onViewDetails,
    onEditRecipe,
    ActionsComponent,
    dragProps = null,
    dragStyle = null,
    extraActions = null
}) => {
    const { t } = useTranslation();
    // Verificamos que los datos del plato sean válidos
    if (!meal || !meal.name || !meal.ingredients) return null;

    const getMealTypeIcon = (mealType) => {
        switch (mealType) {
            case 'breakfast': return '🌅';
            case 'lunch': return '☀️';
            case 'dinner': return '🌙';
            default: return '🍽️';
        }
    };

    const getMealTypeName = (mealType) => {
        switch (mealType) {
            case 'breakfast': return t('meal.types.breakfast');
            case 'lunch': return t('meal.types.lunch');
            case 'dinner': return t('meal.types.dinner');
            default: return mealType;
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Sin fecha';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <div ref={dragProps?.setNodeRef} style={dragStyle}>
            <Card className="bg-gradient-to-br from-white to-gray-50 border-gray-200 shadow-md hover:shadow-lg transition-shadow" padding="lg">
                {/* Componente de acciones inyectado */}
                {ActionsComponent && (
                    <div className="flex gap-2 flex-shrink-0 w-full justify-end mb-4">
                        <ActionsComponent
                            meal={meal}
                            mealKey={mealKey}
                            evaluatedMeals={evaluatedMeals}
                            repeatableStates={repeatableStates}
                            onFeedback={onFeedback}
                            onToggleRepeatable={onToggleRepeatable}
                        />
                    </div>
                )}

                {/* Header de la comida */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-4 sm:mb-6 gap-3">
                    <div className="flex-1 min-w-0 w-full">
                        <div className="flex items-center gap-2 sm:gap-3 mb-2 flex-wrap">
                            {canReorder && dragProps && (
                                <div
                                    {...dragProps.attributes}
                                    {...dragProps.listeners}
                                    className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-200 rounded transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center flex-shrink-0"
                                    title="Arrastra para reordenar"
                                >
                                    <span className="text-lg sm:text-xl">☰</span>
                                </div>
                            )}
                            <span className="text-xl sm:text-2xl flex-shrink-0">
                                {getMealTypeIcon(meal.mealType)}
                            </span>
                            <span className="text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-wide">
                                {getMealTypeName(meal.mealType)}
                            </span>
                            {meal.isArchived && (
                                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full whitespace-nowrap">
                                    {t('menu.display.archive')}
                                </span>
                            )}
                        </div>
                        <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 leading-tight break-words">
                            {meal.name}
                        </h3>
                    </div>
                </div>

                {/* Botón de repetir semanalmente - solo si el meal tiene ID y no hay ActionsComponent */}
                {/* {meal.id && onToggleRepeatable && !ActionsComponent && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                        <Tooltip
                            content={t('menu.display.repeatWeeklyTooltip')}
                            position="top"
                        >
                            <Button
                                variant={repeatableStates[meal.id] ? "primary" : "ghost"}
                                size="sm"
                                onClick={() => onToggleRepeatable(meal.id)}
                                className={`w-full min-h-[44px] text-sm sm:text-base ${repeatableStates[meal.id]
                                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                    : 'text-blue-600 hover:bg-blue-50 border border-blue-200'
                                    }`}
                            >
                                {repeatableStates[meal.id] ? (
                                    <>
                                        <span className="mr-2">✓</span>
                                        <span className="hidden sm:inline">{t('menu.display.repeatWeekly')}</span><span className="sm:hidden">{t('menu.display.repeatWeekly')}</span>
                                    </>
                                ) : (
                                    <>
                                        <span className="mr-2">📅</span>
                                        <span className="hidden sm:inline">{t('menu.display.repeatWeekly')}</span><span className="sm:hidden">{t('menu.display.repeatWeekly')}</span>
                                    </>
                                )}
                            </Button>
                        </Tooltip>
                    </div>
                )} */}

                {/* Acciones extra (inyectadas desde el componente padre) */}
                {extraActions && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                        {extraActions}
                    </div>
                )}

                {/* Ingredientes */}
                <div>
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3 gap-2">
                        <p className="font-medium text-gray-600 flex items-center text-sm sm:text-base">
                            <span className="mr-2">🥘</span>
                            {t('menu.edit.ingredientsLabel')}
                        </p>
                        {/* Solo mostrar botones si NO hay ActionsComponent */}
                        {!ActionsComponent && (
                            <div className="flex gap-2 flex-wrap">
                                {onEditRecipe && meal.id && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => onEditRecipe(meal)}
                                        className="text-green-600 hover:bg-green-50 border border-green-200 min-h-[44px] text-xs sm:text-sm flex-1 sm:flex-initial"
                                    >
                                        <span className="hidden sm:inline">{t('common.edit')}</span>
                                    </Button>
                                )}
                                {onViewDetails && meal.id && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => onViewDetails(meal.recipe_id || meal.id)}
                                        className="text-blue-600 hover:bg-blue-50 border border-blue-200 min-h-[44px] text-xs sm:text-sm flex-1 sm:flex-initial"
                                    >
                                        <span className="mr-2">👁️</span>
                                        {t('menu.display.viewRecipe') || 'Ver receta'}
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {meal.ingredients.map((ing, index) => (
                            <div key={index} className="bg-white rounded-lg border border-gray-200 p-2 sm:p-3 hover:shadow-sm transition-shadow">
                                <div className="flex items-center justify-between gap-2">
                                    <span className="font-medium text-gray-800 text-xs sm:text-sm flex-1 min-w-0 truncate" title={ing.item}>{ing.item}</span>
                                    <span className="bg-primary-100 text-primary-700 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0">
                                        {ing.quantity}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default MealCard;
